import React, { useState } from "react";
import { api, apiErr } from "../lib/api";
import { useI18n } from "../i18n/I18nContext";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import { toast } from "sonner";
import { CheckCircle2, Check, Users } from "lucide-react";

export default function SeminairesPage() {
  const { t } = useI18n();
  const [form, setForm] = useState({
    nom: "", prenom: "", entreprise: "", email: "", telephone: "",
    type_evenement: "", date_souhaitee: "", date_flexible: false,
    participants: "", hebergement: "", nuits: "", budget: "", commentaire: "", consent: false,
  });
  const [repas, setRepas] = useState([]);
  const [equipements, setEquipements] = useState([]);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const toggle = (arr, setArr, v) => setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const repasOpts = ["Paniers pique-nique", "Plats cuisinés à emporter"];
  const equipOpts = ["Vidéoprojecteur", "Écran", "Paperboard", "Wi-Fi", "Disposition de salle"];

  const label = "text-sm font-medium text-ink-700 mb-1.5 block";
  const input = "w-full rounded-xl border border-ink-400/25 bg-white px-4 py-2.5 text-sm text-ink outline-none focus:border-brand focus:ring-1 focus:ring-brand";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error(t("form.consent")); return; }
    setSending(true);
    try {
      await api.post("/quote", {
        ...form,
        participants: form.participants ? Number(form.participants) : null,
        hebergement: form.hebergement ? Number(form.hebergement) : null,
        nuits: form.nuits ? Number(form.nuits) : null,
        repas, equipements,
      });
      setDone(true);
      toast.success(t("form.quoteSuccess"));
      window.scrollTo({ top: document.getElementById("devis").offsetTop - 80, behavior: "smooth" });
    } catch (err) { toast.error(apiErr(err)); }
    finally { setSending(false); }
  };

  return (
    <div>
      <PageHero title={t("seminarPage.title")} subtitle={t("seminarPage.subtitle")} image="/api/files/begaudiere/uploads/51096ab6-e00e-4533-a3ab-103ed39f9ea4.png" crumb={t("nav.seminaires")} />

      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <Reveal>
            <h2 className="font-heading text-3xl sm:text-4xl text-ink-900 mb-5">{t("seminarPage.s1t")}</h2>
            <p className="text-ink-600 font-body text-lg leading-relaxed">{t("seminarPage.s1x")}</p>
          </Reveal>
          <Reveal delay={140}>
            <figure className="relative rounded-3xl overflow-hidden shadow-[0_20px_60px_rgba(44,48,48,0.18)] ring-1 ring-black/5" data-testid="seminar-room-photo">
              <img
                src="/api/files/begaudiere/uploads/51096ab6-e00e-4533-a3ab-103ed39f9ea4.png"
                alt="Salle de réception et de séminaire du Domaine de La Bégaudière"
                className="w-full h-[360px] sm:h-[440px] object-cover"
                loading="lazy"
              />
              <figcaption className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-ink-900/70 to-transparent px-5 py-4 text-white text-sm font-body">
                Notre salle de réception, séminaires et formations
              </figcaption>
            </figure>
          </Reveal>
        </div>
      </section>

      {/* PRIVATISATION */}
      <section className="py-16 lg:py-24 bg-brand-900 text-white" data-testid="seminar-privatisation">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-3xl mb-10">
            <p className="text-brand-200 font-accent text-2xl italic mb-3">{t("nav.seminaires")}</p>
            <h2 className="font-heading text-3xl sm:text-4xl mb-4">{t("seminarPage.privatTitle")}</h2>
            <p className="text-white/80 font-body leading-relaxed">{t("seminarPage.privatText")}</p>
          </Reveal>

          <div className="grid grid-cols-3 gap-4 mb-10 max-w-2xl">
            {t("seminarPage.privatStats").map((s, i) => (
              <Reveal key={i} delay={i * 90}>
                <div className="rounded-2xl bg-white/[0.06] border border-white/10 px-4 py-6 text-center">
                  <div className="font-heading text-4xl sm:text-5xl text-brand-200">{s.n}</div>
                  <div className="text-xs sm:text-sm text-white/70 mt-1">{s.l}</div>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
            {t("seminarPage.privatItems").map((it) => (
              <div key={it} className="flex items-center gap-2.5 text-sm text-white/90 font-body">
                <span className="w-6 h-6 rounded-full bg-white/10 ring-1 ring-white/15 flex items-center justify-center shrink-0">
                  <Check size={14} className="text-brand-200" />
                </span>
                {it}
              </div>
            ))}
          </div>

          <a href="#devis" className="mt-10 inline-flex items-center gap-2 rounded-full bg-white text-brand-900 px-6 py-3 font-medium hover:bg-white/90 transition-colors" data-testid="seminar-privat-cta">
            <Users size={18} /> {t("seminarPage.formTitle")}
          </a>
        </div>
      </section>

      <section className="py-14 bg-beige/60">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-12">
          <Reveal>
            <h3 className="font-heading text-2xl text-ink-900 mb-5">{t("seminarPage.typesTitle")}</h3>
            <div className="flex flex-wrap gap-2">
              {t("seminarPage.types").map((x) => <span key={x} className="rounded-full bg-white border border-black/5 px-4 py-2 text-sm text-ink-700">{x}</span>)}
            </div>
          </Reveal>
          <Reveal delay={120}>
            <h3 className="font-heading text-2xl text-ink-900 mb-5">{t("seminarPage.prestaTitle")}</h3>
            <div className="flex flex-wrap gap-2">
              {t("seminarPage.presta").map((x) => <span key={x} className="rounded-full bg-white border border-black/5 px-4 py-2 text-sm text-ink-700">{x}</span>)}
            </div>
            <p className="text-xs text-ink-400 mt-4">{t("seminarPage.prestaNote")}</p>
          </Reveal>
        </div>
      </section>

      <section id="devis" className="py-16 lg:py-24">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-10">
            <h2 className="font-heading text-3xl sm:text-4xl text-ink-900 mb-3">{t("seminarPage.formTitle")}</h2>
            <p className="text-ink-600 font-body">{t("seminarPage.formSubtitle")}</p>
          </Reveal>

          {done ? (
            <div className="bg-white rounded-2xl border border-black/5 p-12 text-center">
              <CheckCircle2 size={52} className="mx-auto text-brand mb-4" strokeWidth={1.5} />
              <p className="text-ink-600 font-body text-lg">{t("form.quoteSuccess")}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white rounded-2xl border border-black/5 shadow-[0_8px_32px_rgba(44,48,48,0.06)] p-7 sm:p-9 space-y-5" data-testid="quote-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className={label}>{t("form.nom")} *</label><input required value={form.nom} onChange={set("nom")} className={input} data-testid="quote-nom" /></div>
                <div><label className={label}>{t("form.prenom")}</label><input value={form.prenom} onChange={set("prenom")} className={input} /></div>
              </div>
              <div><label className={label}>{t("form.entreprise")}</label><input value={form.entreprise} onChange={set("entreprise")} className={input} /></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className={label}>{t("form.email")} *</label><input type="email" required value={form.email} onChange={set("email")} className={input} data-testid="quote-email" /></div>
                <div><label className={label}>{t("form.telephone")}</label><input value={form.telephone} onChange={set("telephone")} className={input} /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={label}>{t("form.typeEvenement")}</label>
                  <select value={form.type_evenement} onChange={set("type_evenement")} className={input} data-testid="quote-type">
                    <option value="">Sélectionner…</option>
                    {t("seminarPage.types").map((x) => <option key={x} value={x}>{x}</option>)}
                  </select>
                </div>
                <div><label className={label}>{t("form.dateSouhaitee")}</label><input type="date" value={form.date_souhaitee} onChange={set("date_souhaitee")} className={input} /></div>
              </div>
              <label className="flex items-center gap-2 text-sm text-ink-600"><input type="checkbox" checked={form.date_flexible} onChange={set("date_flexible")} className="accent-brand w-4 h-4" /> {t("form.dateFlexible")}</label>
              <div className="grid sm:grid-cols-3 gap-4">
                <div><label className={label}>{t("form.participants")}</label><input type="number" min="1" value={form.participants} onChange={set("participants")} className={input} /></div>
                <div><label className={label}>{t("form.hebergement")}</label><input type="number" min="0" value={form.hebergement} onChange={set("hebergement")} className={input} /></div>
                <div><label className={label}>{t("form.nuits")}</label><input type="number" min="0" value={form.nuits} onChange={set("nuits")} className={input} /></div>
              </div>
              <div>
                <label className={label}>{t("form.repas")}</label>
                <div className="flex flex-wrap gap-2">
                  {repasOpts.map((x) => (
                    <button type="button" key={x} onClick={() => toggle(repas, setRepas, x)} className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm border transition-colors ${repas.includes(x) ? "bg-brand text-white border-brand" : "border-ink-400/30 text-ink-600"}`}>
                      {repas.includes(x) && <Check size={14} />}{x}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className={label}>{t("form.equipements")}</label>
                <div className="flex flex-wrap gap-2">
                  {equipOpts.map((x) => (
                    <button type="button" key={x} onClick={() => toggle(equipements, setEquipements, x)} className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm border transition-colors ${equipements.includes(x) ? "bg-brand text-white border-brand" : "border-ink-400/30 text-ink-600"}`}>
                      {equipements.includes(x) && <Check size={14} />}{x}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className={label}>{t("form.budget")}</label>
                <select value={form.budget} onChange={set("budget")} className={input} data-testid="quote-budget">
                  <option value="">Sélectionner…</option>
                  {t("form.budgetOptions").map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
                <p className="text-xs text-ink-400 mt-1.5">{t("form.budgetHint")}</p>
              </div>
              <div><label className={label}>{t("form.commentaire")}</label><textarea rows={4} value={form.commentaire} onChange={set("commentaire")} className={input} data-testid="quote-comment" /></div>
              <label className="flex items-start gap-2 text-sm text-ink-600"><input type="checkbox" checked={form.consent} onChange={set("consent")} className="accent-brand w-4 h-4 mt-0.5" data-testid="quote-consent" /> {t("form.consent")}</label>
              <button type="submit" disabled={sending} className="w-full rounded-full bg-brand text-white py-3.5 font-medium hover:bg-brand-600 transition-colors disabled:opacity-60" data-testid="quote-submit">
                {sending ? t("common.loading") : t("seminarSection.cta")}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
